<template>
	<ActionCard
		:bordered="!embedded"
		clickable
		:embedded
		:size="cardSize"
		class="@container"
		:vertical
		:segmented
		:card-class
		:content-class
		:extra-class
		:hide-extra-slot
		header-class="grow"
		@click="routePatients({ patientId: patient.id, patientName: `${patient.name} ${patient.surname}` }).navigate()"
	>
		<template #image>
			<div v-if="size === 'tiny'" class="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 @xl:w-auto">
				<PatientAvatar :patient :size="28" resize-initials />
			</div>
			<div v-else-if="size === 'small'" class="flex flex-col gap-2">
				<PatientAvatar :patient :size="34" />

				<div class="text-fs-md font-medium">{{ patient.name }} {{ patient.surname }}</div>
			</div>
			<div v-else class="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-3 @xl:w-auto">
				<PatientAvatar :patient :size="40" class="flex flex-col items-center justify-center" />

				<div class="block @xl:hidden">
					<DeliveryDaysTag v-if="patient.meta.status === 'PREGNANCY'" :meta="patient.meta" size="small" />
				</div>
			</div>
		</template>
		<template #title>
			<div v-if="size === 'tiny'" class="flex flex-col gap-2">
				<div class="flex items-center gap-2">
					<div class="text-fs-sm">{{ patient.name }} {{ patient.surname }}</div>
				</div>
				<div v-if="!hideStatus">
					<StatusTag :meta="patient.meta" size="small" />
				</div>
			</div>

			<div v-else-if="size === 'small'" class="flex items-start justify-end pt-0.5">
				<JMButton type="secondary" class="w-7! px-0!" size="small" @click.stop="showDrawer = true">
					<template #icon>
						<Icon name="carbon:side-panel-close" :size="16" />
					</template>
				</JMButton>
			</div>

			<div v-else class="flex flex-col gap-2">
				<div class="flex items-center gap-2">
					<div>{{ patient.name }} {{ patient.surname }}</div>
					<StatusTag v-if="!hideStatus" :meta="patient.meta" size="small" />
				</div>
				<div v-if="shortDiagnosis" class="text-tertiary text-fs-sm leading-tight font-normal">
					{{ shortDiagnosis }}
				</div>
				<div class="flex flex-wrap items-center gap-1.5">
					<PatientMetaTags :meta="patient.meta" />
				</div>
			</div>
		</template>
		<template #extra>
			<template v-if="size === 'tiny'">
				<JMButton type="secondary" class="w-7! px-0!" size="small" @click.stop="showDrawer = true">
					<template #icon>
						<Icon name="carbon:side-panel-close" :size="16" />
					</template>
				</JMButton>
			</template>
			<template v-else-if="size === 'small'">
				<div class="flex flex-col gap-1">
					<div class="text-fs-sm text-secondary">{{ metaLabel }}</div>
					<div class="text-fs-sm text-secondary font-medium">{{ metaValue }}</div>
				</div>
			</template>
			<template v-else>
				<div class="hidden @xl:block">
					<DeliveryDaysTag v-if="patient.meta.status === 'PREGNANCY'" :meta="patient.meta" size="small" />
				</div>

				<JMButton type="secondary" size="small" @click.stop="showDrawer = true">
					<template #icon>
						<Icon name="carbon:side-panel-close" :size="16" />
					</template>
					{{ $t(`common.misc.preview`) }}
				</JMButton>
			</template>

			<n-drawer v-model:show="showDrawer" :width="440" class="max-w-[90vw]" :trap-focus="false">
				<n-drawer-content
					:title="$t(`patients.drawer.patient_preview`)"
					closable
					:native-scrollbar="false"
					body-content-class="p-0!"
				>
					<PatientOverview :patient />
				</n-drawer-content>
			</n-drawer>
		</template>
	</ActionCard>
</template>

<script setup lang="ts">
import type { I18nString } from "@/lang/config"
import type { Patient } from "@/types/patient"
import { NDrawer, NDrawerContent } from "naive-ui"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import ActionCard from "@/components/common/ActionCard.vue"
import Icon from "@/components/common/Icon.vue"
import JMButton from "@/components/common/JMButton.vue"
import { useNavigation } from "@/composables/router/useNavigation"
import { getGestationalLabel } from "@/utils/pregnancyCalculator"
import PatientAvatar from "./PatientAvatar.vue"
import PatientMetaTags from "./PatientMetaTags"
import DeliveryDaysTag from "./PatientMetaTags/tags/DeliveryDays.vue"
import StatusTag from "./PatientMetaTags/tags/Status.vue"
import PatientOverview from "./PatientOverview.vue"

export type PatientCardMeta = "gestational_age"
export type PatientCardSize = "tiny" | "small" | "medium" | "large"

const {
	patient,
	size = "medium",
	hideStatus,
	embedded = false,
	meta
} = defineProps<{
	patient: Patient
	size?: PatientCardSize
	hideStatus?: boolean
	embedded?: boolean
	meta?: PatientCardMeta
}>()

const { routePatients } = useNavigation()
const { t } = useI18n()
const shortDiagnosis = computed(() => patient.notes || "")
const showDrawer = ref(false)

const vertical = computed(() => size === "small")
const segmented = computed(() => size === "small")

const cardSize = computed(() => {
	if (embedded) {
		return "small"
	}
	switch (size) {
		case "tiny":
			return "small"
		case "small":
			return "small"
		case "medium":
			return "medium"
		case "large":
			return "large"
		default:
			return "medium"
	}
})

const cardClass = computed(() => {
	switch (size) {
		case "tiny":
			return "items-stretch @xl:flex-row"
		case "small":
			return "p-3! items-stretch"
		default:
			return "items-stretch @xl:flex-row flex-col"
	}
})
const contentClass = computed(() => {
	switch (size) {
		case "tiny":
			return "grow gap-3.5!"
		case "small":
			return "grow flex-row justify-between"
		default:
			return "grow flex-col items-start gap-8! @xl:flex-row"
	}
})
const extraClass = computed(() => {
	switch (size) {
		case "tiny":
			return "items-center!"
		case "small":
			return "flex flex-col gap-3!"
		default:
			return "flex flex-col items-end! justify-between gap-2!"
	}
})

const metaLabel = computed(() => {
	switch (meta) {
		case "gestational_age":
			return t<I18nString>(`patients.tags.gestational_age`)
		default:
			return ""
	}
})

const metaValue = computed(() => {
	switch (meta) {
		case "gestational_age":
			return getGestationalLabel(patient.meta.gestational_period || { weeks: 0, days: 0 }, { t })
		default:
			return ""
	}
})

const hideExtraSlot = computed(() => size === "small" && !meta)
</script>
